import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../components/ui/table";
import { 
  ArrowLeft, Building2, Briefcase, Plus, X, Trash2, 
  Save, Mail, Edit3, Loader2, Globe, Phone 
} from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import Button from "../components/ui/button/Button";
import brandService from "../api/brandService";

export default function EntityServiceDetail({ isDark }) {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const hasFetched = useRef(false);

  const secondaryText = isDark ? 'text-slate-300' : 'text-slate-600';
  const brandBlue = isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]';
  const brandBlueBg = isDark ? 'bg-[#5A9AFF]' : 'bg-[#2D4D94]';

  const toastStyle = {
    style: {
      borderRadius: '12px',
      background: isDark ? '#020617' : '#ffffff', 
      color: isDark ? '#cbd5e1' : '#475569',      
      border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontWeight: '800',
      padding: '12px 20px',
    },
  };

  const [brandForm, setBrandForm] = useState({
    brand_name: "", email: "", website: "", phone_number: "", is_active: 1
  });
  const [services, setServices] = useState([]);
  const [serviceForm, setServiceForm] = useState({ service_name: "" });

  const fetchBrandData = async () => {
    try {
      const res = await brandService.getBrandDetail(id);
      if (res) {
        setBrandForm({
          brand_name: res.brand_name || "",
          email: res.email || "",
          website: res.website || "",
          phone_number: res.phone_number || "",
          is_active: res.is_active !== undefined ? parseInt(res.is_active) : 1
        });
        setServices(res.services || []);
      }
    } catch (err) { console.error("Error loading brand", err); } 
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isNew && !hasFetched.current) {
      fetchBrandData();
      hasFetched.current = true;
    }
  }, [id, isNew]);

  const handleSaveProfile = async () => {
    const loadingToast = toast.loading(isNew ? "CREATING BRAND..." : "UPDATING PROFILE...", toastStyle);
    try {
      if (isNew) {
        const res = await brandService.createBrand(brandForm);
        if (res && res.id) {
          // ADD DURATION HERE
          toast.success("BRAND CREATED", { ...toastStyle, id: loadingToast, duration: 3000 });
          navigate(`/brands/${res.id}`, { replace: true });
        }
      } else {
        await brandService.updateBrand(id, brandForm);
        // ADD DURATION HERE
        toast.success("PROFILE UPDATED", { ...toastStyle, id: loadingToast, duration: 3000 });
      }
    } catch (err) { 
      // ADD DURATION HERE
      toast.error("SAVE FAILED", { ...toastStyle, id: loadingToast, duration: 4000 }); 
    }
};

const handleSaveService = async () => {
    if (!serviceForm.service_name.trim()) { 
        toast.error("SERVICE NAME REQUIRED", toastStyle); 
        return; 
    }
    const loadingToast = toast.loading("SAVING SERVICE...", toastStyle);
    try {
      if (editingId) {
        await brandService.updateService(editingId, { service_name: serviceForm.service_name });
        // ADD DURATION HERE
        toast.success("SERVICE UPDATED", { ...toastStyle, id: loadingToast, duration: 3000 });
      } else {
        await brandService.addService(id, { service_name: serviceForm.service_name });
        // ADD DURATION HERE
        toast.success("SERVICE ADDED", { ...toastStyle, id: loadingToast, duration: 3000 });
      }
      fetchBrandData(); 
      resetForm();
    } catch (err) { 
      // ADD DURATION HERE
      toast.error("SAVE FAILED", { ...toastStyle, id: loadingToast, duration: 4000 }); 
    }
};

  const handleDeleteService = async (serviceId) => {
    toast((t) => (
      <div className="flex flex-col gap-3">
        <p className="font-bold text-[10px] tracking-widest">DELETE THIS SERVICE?</p>
        <div className="flex gap-2">
          <button onClick={async () => {
            toast.dismiss(t.id);
            const loadingToast = toast.loading("DELETING...", toastStyle);
            try {
              await brandService.deleteService(serviceId);
              toast.success("SERVICE DELETED", { ...toastStyle, id: loadingToast });
              fetchBrandData();
            } catch (err) { toast.error("DELETE FAILED", { ...toastStyle, id: loadingToast }); }
          }} className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase">Confirm</button>
          <button onClick={() => toast.dismiss(t.id)} className="bg-slate-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase">Cancel</button>
        </div>
      </div>
    ), { ...toastStyle, duration: 6000, icon: <Trash2 size={20} className="text-orange-500" /> });
  };

const handleDeleteBrand = async () => {
  const stats = await brandService.getBrandStats(id);
  const taskCount = stats?.task_count || 0;

  toast((t) => (
    <div className="flex flex-col gap-3">
      <div className="space-y-1">
        <p className="font-bold text-[10px] tracking-widest text-orange-600 uppercase">
          Confirm Brand Purge
        </p>
        <p className="text-[11px] text-slate-500 leading-normal normal-case">
          Warning: This will also delete <span className="text-orange-600">{taskCount} tasks</span> and all linked services.
        </p>
      </div>

      <div className="flex gap-2">
        <button 
          onClick={async () => {
            // 1. Dismiss the confirmation UI
            toast.dismiss(t.id);
            
            // 2. Start loading
            const loadingId = toast.loading("PURGING DATA...", toastStyle);
            
            try {
              await brandService.deleteBrand(id);
              
              // 3. Transform to success AND set duration
              toast.success(`DELETED BRAND & ${taskCount} TASKS`, { 
                ...toastStyle, 
                id: loadingId, 
                duration: 3000 // THIS IS THE KEY: it tells the global toaster to auto-hide
              });
              
              // 4. Navigate away
              setTimeout(() => {
                navigate('/brands');
              }, 1000);
            } catch (err) { 
              toast.error("DELETE FAILED", { ...toastStyle, id: loadingId, duration: 4000 }); 
            }
          }} 
          className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg active:scale-95"
        >
          Confirm Wipeout
        </button>
        <button 
          onClick={() => toast.dismiss(t.id)} 
          className="bg-slate-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase opacity-70 hover:opacity-100"
        >
          Cancel
        </button>
      </div>
    </div>
  ), { ...toastStyle, duration: 4000, icon: <Trash2 size={16} className="text-orange-600 shrink-0" /> });
};

  const handleEdit = (service) => {
    setServiceForm({ service_name: service.service_name });
    setEditingId(service.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setServiceForm({ service_name: "" });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return (
    <div className={`h-screen flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
      <Loader2 className={`animate-spin ${isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}`} size={40} />
    </div>
  );

  return (
    <div className={`p-4 md:p-6 space-y-6 min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
      
      {/* Back Button with hover effects */}
      <div className="max-w-7xl mx-auto w-full">
        <button 
          onClick={() => navigate('/brands')} 
          className={`group w-12 h-12 flex items-center justify-center rounded-xl transition-all border shadow-sm active:scale-95
            ${isDark ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800 text-[#5A9AFF] hover:text-orange-600' 
                     : 'bg-white border-slate-200 hover:bg-slate-50 text-[#2D4D94] hover:text-orange-600'}`}
        >
          <ArrowLeft size={24} strokeWidth={2.5} className="transition-transform group-hover:-translate-x-1" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start max-w-7xl mx-auto">
        <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-6">
          <div className={`border rounded-[24px] p-6 shadow-sm space-y-6 transition-all ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h3 className="text-[12px] font-black uppercase tracking-[0.1em] flex gap-1.5">
  <span className="text-orange-600">Brand</span>
  <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>Profile</span>
</h3>
            
            <div className="space-y-4">
              <DetailInput isDark={isDark} label="Name" icon={Building2} value={brandForm.brand_name} onChange={(v) => setBrandForm({...brandForm, brand_name: v})} />
              <DetailInput isDark={isDark} label="Email" icon={Mail} value={brandForm.email} onChange={(v) => setBrandForm({...brandForm, email: v})} />
              <DetailInput isDark={isDark} label="Phone Number" icon={Phone} value={brandForm.phone_number} onChange={(v) => setBrandForm({...brandForm, phone_number: v})} />
              <DetailInput isDark={isDark} label="Website URL" icon={Globe} value={brandForm.website} onChange={(v) => setBrandForm({...brandForm, website: v})} />

              {/* Status Toggle */}
              <div className={`flex items-center justify-between p-4 rounded-xl border border-dashed transition-colors ${isDark ? 'border-[#5A9AFF]/30' : 'border-[#2D4D94]/30'}`}>
                <div className="flex flex-col">
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${secondaryText}`}>Status</span>
                  <span className={`text-xs font-bold ${brandForm.is_active ? (isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]') : 'text-orange-600'}`}>
                    {brandForm.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
                <button
                  onClick={() => {
                      const nextState = brandForm.is_active ? 0 : 1;
                      setBrandForm({ ...brandForm, is_active: nextState });
                      toast.success(nextState ? "BRAND ACTIVATED" : "BRAND DEACTIVATED", toastStyle);
                  }}
                  className={`w-11 h-6 rounded-full transition-all duration-300 relative ${brandForm.is_active ? brandBlueBg : 'bg-orange-200'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 ${brandForm.is_active ? 'left-6' : 'left-1'}`} />
                </button>
              </div>

{/* Action Buttons: 80/20 Split */}
              <div className="flex items-center gap-2 mt-2 w-full">
                <Button 
                  isDark={isDark} 
                  onClick={handleSaveProfile} 
                  className={`group flex items-center justify-center py-4 font-bold text-xs uppercase tracking-widest rounded-xl transition-all border shadow-sm active:scale-95
                    ${isNew ? 'w-full' : 'w-[80%]'}
                    ${isDark ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                >
                  <Save size={16} className="text-orange-600 mr-2 transition-transform group-hover:scale-110" /> 
                  <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>
                    {isNew ? "Create Brand" : "Update"}
                  </span>
                </Button>

                {!isNew && (
                  <button 
                    onClick={handleDeleteBrand}
                    title="Delete Brand"
                    className={`group w-[20%] h-[52px] flex items-center justify-center rounded-xl transition-all border shadow-sm active:scale-95
                      ${isDark 
                        ? 'bg-slate-900/50 border-slate-700 hover:bg-rose-950/20 text-rose-400' 
                        : 'bg-white border-slate-200 hover:bg-rose-50 text-rose-600'}`}
                  >
                    <Trash2 size={18} className="text-orange-600 transition-transform group-hover:scale-110 group-hover:text-rose-500" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-6">
          {!isNew && !showForm && (
            <div className="space-y-6">
              <div className="flex items-center justify-between px-1">
                {/* 1. Header Camel Case & Split Color */}
                <h2 className="text-xl font-semibold tracking-tight">
                  <span className="text-orange-600">S</span><span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>ervices</span>
                </h2>
                {/* Glass Button with 90deg animation */}
                <Button 
                  isDark={isDark}
                  onClick={() => setShowForm(true)} 
                  className={`group flex items-center px-5 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-md active:scale-95
                    ${isDark ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                >
                  <Plus size={18} strokeWidth={3} className="text-orange-600 transition-transform duration-500 ease-out group-hover:rotate-90" /> 
                  <span className={`ml-2 ${isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}`}>Add Service</span>
                </Button>
              </div>

              <div className={`rounded-[24px] border overflow-hidden shadow-sm transition-all ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                <Table className="w-full text-left">
                  <TableHeader className={isDark ? 'bg-slate-800/50' : 'bg-gray-50/50'}>
                    <TableRow className={isDark ? 'border-slate-800' : 'border-slate-200'}>
                      <TableCell isHeader className={`px-6 py-4 text-[10px] font-bold uppercase tracking-widest ${secondaryText}`}>Service name</TableCell>
                      <TableCell isHeader className={`px-6 py-4 text-right text-[10px] font-bold uppercase tracking-widest ${secondaryText}`}>Actions</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {services.map((s) => (
                      <TableRow key={s.id} className={`group transition-colors border-b last:border-none ${isDark ? 'hover:bg-slate-800/50 border-slate-800' : 'hover:bg-blue-50/30 border-gray-50'}`}>
                        <TableCell className="px-6 py-5">
                          {/* 4. Service Name Camel Case */}
                          <div className={`font-bold capitalize text-sm ${isDark ? 'text-slate-100 group-hover:text-[#5A9AFF]' : 'text-gray-900 group-hover:text-[#2D4D94]'}`}>
                            {s.service_name.toLowerCase()}
                          </div>
                        </TableCell>
                        <TableCell className="px-6 py-5 text-right">
                          <div className="flex justify-end items-center gap-1">
                            {/* 3. Pen (Blue variant) and Trash (Orange) */}
                            <button onClick={() => handleEdit(s)} className={`p-2 rounded-lg transition-all active:scale-90 ${isDark ? 'text-[#5A9AFF] hover:bg-[#5A9AFF]/10' : 'text-[#2D4D94] hover:bg-blue-50'}`}>
                              <Edit3 size={18} />
                            </button>
                            <button onClick={() => handleDeleteService(s.id)} className="text-orange-500 hover:text-orange-600 transition-all active:scale-90 p-2">
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {showForm && (
            <div className={`border rounded-[24px] p-8 shadow-sm ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-100'}`}>
              <div className="flex justify-between items-center mb-6">
                <h3 className={`text-lg font-bold tracking-tight flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                   <span className="text-orange-600">{editingId ? "Edit" : "Register"}</span> <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>Service</span>
                </h3>
                <button onClick={resetForm} className={`${secondaryText} hover:scale-110 transition-transform`}><X size={20} /></button>
              </div>
              <div className="grid grid-cols-1 gap-6">
                <DetailInput isDark={isDark} label="Service name" icon={Briefcase} value={serviceForm.service_name} onChange={(v) => setServiceForm({...serviceForm, service_name: v})} placeholder="e.g. Corporate training" />
                <div className="flex justify-end">
                  <Button onClick={handleSaveService} className={`group flex items-center px-10 py-4 font-bold uppercase text-xs tracking-widest rounded-xl transition-all border shadow-sm active:scale-95
                    ${isDark ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                    <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>{editingId ? "Update service" : "Save service"}</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailInput({ label, icon: Icon, isDark, onChange, ...props }) {
  const brandBlue = isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]';
  return (
    <div className="text-left w-full">
      <label className={`text-[10px] font-bold uppercase block mb-1.5 ml-1 tracking-widest transition-colors ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
        {label}
      </label>
      <div className="relative">
        <Icon size={14} className={`absolute left-4 top-1/2 -translate-y-1/2 ${brandBlue}`} />
        <input 
          {...props} 
          onChange={(e) => onChange?.(e.target.value)}
          className={`w-full rounded-xl pl-11 pr-4 py-3 text-xs font-semibold transition-all outline-none border ${
            isDark ? 'bg-slate-950 border-slate-800 text-white focus:border-orange-500/50' 
                   : 'bg-slate-50 border-slate-200 text-gray-900 focus:border-orange-500/50'
          }`} 
        />
      </div>
    </div>
  );
}